import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { AmbassadorAward } from "@/components/admin/AmbassadorAward";

const Flag = ({ ok, label }) => <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${ok ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{label}</span>;

export const ReferralsAdmin = ({ auth, onErr }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/referrals", auth).then((r) => setD(r.data)).catch(onErr), [auth, onErr]);
  useEffect(() => { load(); }, [load]);
  const thank = async (id) => {
    try { await api.post(`/admin/referrals/${id}/thanked`, {}, auth); toast.success("Marked as thanked"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const board = async (r) => {
    try { await api.post(`/admin/referrals/board/${r.ref_code}`, { show: !!r.board_hidden }, auth); toast.success(r.board_hidden ? "Shown on the board" : "Removed from the board"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!d) return <Loader2 className="w-6 h-6 animate-spin mt-8" />;
  return (
    <div className="mt-6 space-y-5" data-testid="referrals-admin">
      <AmbassadorAward auth={auth} onErr={onErr} />
      <div className="flex flex-wrap items-center gap-2">
        <span className="bg-[#0A192F] text-white rounded-xl px-4 py-2 text-sm font-bold" data-testid="referrals-total">{d.total} friends joined via links</span>
        {d.badges.map((b) => <span key={b.name} className="bg-[#FFD166]/40 text-[#0A192F] rounded-full px-3 py-1 text-xs font-bold">{b.name}: {b.min}+</span>)}
        <Button variant="outline" size="sm" onClick={load} className="ml-auto" data-testid="referrals-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>
      <div className="grid lg:grid-cols-[1fr_1.6fr] gap-5">
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="referrers-table">
            <thead className="bg-[#F0F4F8] text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Family", "Code", "Friends", "Badge", "Board"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
            <tbody>{d.referrers.map((r) => (
              <tr key={r.email} className="border-t border-slate-100" data-testid={`referrer-row-${r.ref_code}`}>
                <td className="px-3 py-2">{r.name || "—"}<div className="text-xs text-slate-500">{r.email}</div></td>
                <td className="px-3 py-2 font-mono">{r.ref_code}</td><td className="px-3 py-2 font-bold">{r.friends}</td>
                <td className="px-3 py-2"><span className="bg-[#FFD166] rounded-full px-2 py-0.5 text-xs font-bold">{r.badge}</span></td>
                <td className="px-3 py-2 text-xs">{!r.board_opt_in ? <span className="text-slate-400">not opted in</span>
                  : <button onClick={() => board(r)} className={`font-bold ${r.board_hidden ? "text-slate-500" : "text-emerald-700"}`} data-testid={`board-toggle-${r.ref_code}`}>{r.board_hidden ? "Hidden · show" : "Shown · hide"}</button>}</td>
              </tr>))}
              {!d.referrers.length && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">No referrals yet</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="referrals-table">
            <thead className="bg-[#F0F4F8] text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["When", "Referrer", "Friend", "Thank-you tried", "Status"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
            <tbody>{d.referrals.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`referral-row-${r.id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString("en-IN")}</td>
                <td className="px-3 py-2 text-xs">{r.referrer_email}</td><td className="px-3 py-2">{r.friend_name} · {r.friend_class}</td>
                <td className="px-3 py-2 space-x-1"><Flag ok={r.email_sent} label="Email" /><Flag ok={r.whatsapp} label="WhatsApp" /></td>
                <td className="px-3 py-2">{r.thanked ? <span className="text-xs font-bold text-emerald-700">Thanked</span>
                  : <Button size="sm" variant="outline" className="h-7" onClick={() => thank(r.id)} data-testid={`referral-thank-${r.id}`}><Check className="w-3 h-3 mr-1" />Mark thanked</Button>}</td>
              </tr>))}
              {!d.referrals.length && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">No friends have joined via a link yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
